import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Base from '@mui/material/Dialog'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, breakpoints, zIndex } from '@/styles'
import { CssUnit } from '@/types'
import ModalClose from './modal-close'
import { ModalHeader, ModalFooter } from './parts'
import Sheet from './sheet'
import { useModalize } from '../'

const Content = styled.div.withConfig<{
  overflowMobile: string
  overflowDesktop: string
}>({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    !['overflowMobile', 'overflowDesktop'].includes(prop) && defaultValidatorFn(prop)
})`
  flex: 1 1 auto;
  overflow-y: ${props => props.overflowDesktop};
  position: relative;

  user-select: auto;
  overscroll-behavior: contain;
  -webkit-tap-highlight-color: revert;
  -webkit-touch-callout: revert;
  -webkit-overflow-scrolling: touch;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    overflow-y: ${props => props.overflowMobile};
  }
`

const CloseModal = styled(ModalClose)`
  ${props =>
    props.absolute
      ? `
    position: absolute;
    width: 100%;
  `
      : `
    position: sticky;
    top: env(safe-area-inset-top, 0px);
    height: 0;
  `};

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: env(safe-area-inset-top, 0px);
    right: env(safe-area-inset-right, 0px);
  }
`

type Event = React.MouseEvent<HTMLButtonElement>
type Reason = 'backdropClick' | 'escapeKeyDown' | 'closeButton'

export type ModalProps = {
  width?: CssUnit
  maxWidth?: CssUnit
  maxHeight?: CssUnit
  radius?: CssUnit
  bgColor?: string
  overflow?: string
  showBackground?: boolean
  showClose?: boolean
  clickOutside?: boolean
  onClose?: (e: Event, r?: Reason) => void
  isVideo?: boolean
  sheet?: boolean
  children: React.ReactNode
  hasFooter?: boolean
  hasHeader?: boolean
  overflowMobile?: string
  overflowDesktop?: string
  showFooterOverflow?: boolean
  customIsMobileBreakpoint?: string
  fullSnap?: boolean
  closeConfig?: {
    closeModal: boolean
    caller: () => void
  }
}

export type ModalRefs = {
  titleRef: (el: HTMLDivElement) => void
  footerRef: (el: HTMLDivElement) => void
}

type InternalModalProps = ModalRefs & ModalProps

const Dialog: React.FC<InternalModalProps> = ({
  width = '400px',
  maxWidth = '95%',
  maxHeight = '95vh',
  radius = '20px',
  bgColor = colors.white,
  overflow = 'auto',
  showBackground = true,
  clickOutside = true,
  showClose = true,
  isVideo = false,
  sheet = false,
  onClose,
  closeConfig,
  titleRef,
  footerRef,
  hasFooter,
  hasHeader = true,
  children,
  overflowMobile = 'auto',
  overflowDesktop = 'auto',
  showFooterOverflow = false,
  customIsMobileBreakpoint = null,
  fullSnap = false
}) => {
  const isMobile = useMediaQuery(
    `(max-width: ${customIsMobileBreakpoint ? customIsMobileBreakpoint : breakpoints.ipadMiniMax})`
  )
  const [stacked, setStacked] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const modals = document.querySelectorAll('body > div[aria-modal="true"]')
    const confirmModals = document.querySelectorAll('body > div > div[aria-modal="true"]')
    const amount = modals.length + confirmModals.length
    setStacked(amount > 1)
  }, [setStacked])
  const { visible, close } = useModalize()

  const handleClose = (event: Event, reason: Reason) => {
    // If user can't close modal, don't allow
    // out of focus or escape key to close
    if (!clickOutside) return

    if (typeof closeConfig === 'object') {
      closeConfig.caller()
      return closeConfig.closeModal && close()
    }

    if (typeof onClose === 'function') {
      onClose(event, reason)
    } else {
      close()
    }
  }
  const hasModalBg = bgColor.replace(/\s/g, '') !== 'rgba(0,0,0,0)'
  if (sheet && isMobile) {
    return (
      <Sheet
        hasHeader={hasHeader}
        header={close =>
          hasHeader ? (
            <ModalHeader
              ref={titleRef}
              buttons={<ModalClose top={0} right={0} onClick={close} />}
              isSheet
            ></ModalHeader>
          ) : (
            <div />
          )
        }
        footer={<ModalFooter ref={footerRef} isSheet></ModalFooter>}
        hasFooter={hasFooter}
        fullSnap={fullSnap}
      >
        {children}
      </Sheet>
    )
  }
  const mobileOveflow = overflowMobile !== 'auto' ? overflowMobile : overflow
  const desktopOveflow = overflowDesktop !== 'auto' ? overflowDesktop : overflow

  return (
    <Base
      disablePortal
      open={visible}
      fullScreen={isMobile && !isVideo}
      onClose={handleClose}
      onFocus={e => e.preventDefault()}
      onTouchMove={e => e.preventDefault()}
      transitionDuration={0}
      sx={{
        zIndex: `${zIndex.modal} !important`,
        '& .MuiBackdrop-root': {
          backgroundColor: showBackground ? `rgba(0, 0, 0, ${stacked ? 0.4 : 0.8})` : 'none'
        },
        '& .MuiPaper-root': {
          ...(isMobile && !isVideo
            ? {
                '--dropdown': 'auto',
                height: '100vh',
                '@supports(height: 100dvh)': { height: '100dvh' },
                width: '100%',
                maxWidth: '100%',
                borderRadius: 0,
                backgroundColor: `${bgColor} !important`,
                boxShadow: 'none',
                ...(mobileOveflow !== 'auto' ? { overflow: mobileOveflow } : {})
              }
            : {
                '--dropdown': 'visible',
                width,
                maxWidth,
                maxHeight,
                borderRadius: radius,
                backgroundColor: bgColor,
                boxShadow: hasModalBg ? '0 2px 4px 0 rgba(0, 0, 0, 0.5)' : 'none',
                ...(desktopOveflow !== 'auto' ? { overflow: desktopOveflow } : {})
              })
        }
      }}
    >
      {hasHeader && (
        <ModalHeader
          ref={titleRef}
          buttons={<ModalClose top={0} right={0} onClick={e => handleClose(e, 'closeButton')} />}
          aria-hidden={!sheet}
        ></ModalHeader>
      )}

      <Content
        overflowMobile={overflowMobile !== 'auto' ? overflowMobile : overflow}
        overflowDesktop={overflowDesktop !== 'auto' ? overflowDesktop : overflow}
      >
        {showClose && !sheet && (
          <CloseModal absolute={sheet && isMobile} onClick={e => handleClose(e, 'closeButton')} />
        )}
        {children}
      </Content>
      <ModalFooter
        ref={footerRef}
        aria-hidden={!sheet || !hasFooter}
        showFooterOverflow={showFooterOverflow}
      ></ModalFooter>
    </Base>
  )
}

export default Dialog
